// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

// CareRx AI client helpers — ported from the POT app (public/app.js) so the AI integration
// is faithful: client-side PHI tokenization (the FIRST layer, before anything leaves the
// browser), response detokenization, and AI-output validation.

export interface TokenMap { [token: string]: string } // token -> real value

/**
 * Replace known PHI values with [REDACTED_n] tokens before sending to the AI. Mirrors POT's
 * tokenizePHI: it tokenizes the actual entered patient values (name, MRN, DOB, phone, etc.),
 * not just formats. Returns the cleaned text + the map needed to restore the response.
 */
export function tokenizePHI(text: string, phiValues: string[], existing?: TokenMap): { text: string; map: TokenMap } {
  const map: TokenMap = { ...(existing || {}) }
  if (!text) return { text, map }
  let result = text
  // longest values first so substrings don't partially match
  const vals = [...new Set(phiValues.filter(v => v && v.trim().length >= 2))].sort((a, b) => b.length - a.length)
  let counter = Object.keys(map).length
  for (const val of vals) {
    const v = val.trim()
    if (result.indexOf(v) === -1) continue
    // reuse an existing token for this value if present
    let token = Object.keys(map).find(t => map[t] === v)
    if (!token) { counter++; token = `[REDACTED_${counter}]`; map[token] = v }
    result = result.split(v).join(token)
  }
  return { text: result, map }
}

/** Restore real values in an AI response (so the nurse sees them). Mirrors POT detokenize. */
export function detokenize(text: string, map: TokenMap): string {
  if (!text) return text
  let result = text
  for (const token of Object.keys(map)) result = result.split(token).join(map[token])
  return result
}

// ── AI output validation (ported from POT validateAIOutput) ──────────────────
const ICD10_REGEX = /^[A-TV-Z][0-9][0-9AB](?:\.[0-9A-TV-Z]{1,4})?$/i

export interface AiValidation { status: 'verified' | 'partial' | 'flagged' | 'unverified'; badge: string; warnings: string[] }

export function validateAIOutput(text: string, type: 'icd10' | 'ddi' | 'dose' | 'field_assist' | 'chat'): AiValidation {
  if (type === 'icd10') {
    const matches = (text.match(/\b[A-TV-Z][0-9][0-9AB](?:\.[0-9A-TV-Z]{1,4})?\b/gi) || [])
    const valid = matches.filter(m => ICD10_REGEX.test(m.trim())).length
    const invalid = matches.length - valid
    if (matches.length === 0) return { status: 'unverified', badge: 'No codes found', warnings: [] }
    if (invalid === 0) return { status: 'verified', badge: `${valid} valid code(s)`, warnings: [] }
    return { status: 'partial', badge: `${valid} valid, ${invalid} invalid`, warnings: ['Some ICD-10 codes have invalid format'] }
  }
  if (type === 'ddi') return { status: 'partial', badge: 'AI-generated — verify critical interactions', warnings: [] }
  if (type === 'dose') {
    const warnings: string[] = []
    const re = /(\d+(?:\.\d+)?)\s*(mg|g)\b/gi; let m: RegExpExecArray | null
    while ((m = re.exec(text))) {
      const val = parseFloat(m[1]); const unit = m[2].toLowerCase()
      if (unit === 'g' && val > 10) warnings.push(`Unusually high dose: ${m[0]} — verify against PI`)
      if (unit === 'mg' && val > 5000) warnings.push(`Very high mg dose: ${m[0]} — verify against PI`)
    }
    return warnings.length
      ? { status: 'flagged', badge: 'Dose warning', warnings }
      : { status: 'partial', badge: 'AI-calculated — verify against PI', warnings: [] }
  }
  if (type === 'field_assist') return { status: 'unverified', badge: 'AI-generated — verify before use', warnings: [] }
  return { status: 'unverified', badge: 'AI-generated — verify independently', warnings: [] }
}
